"use client"

import { KeyInsight } from "../../components/key-insight"

interface MetricCardProps {
  value: string
  label: string
  sublabel?: string
}

function MetricCard({ value, label, sublabel }: MetricCardProps) {
  return (
    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-card/80 to-card/40 border border-border/50">
      <div className="text-3xl sm:text-4xl font-bold text-foreground mb-2">{value}</div>
      <div className="text-sm font-medium text-muted-foreground">{label}</div>
      {sublabel && <div className="text-xs text-muted-foreground/70 mt-1">{sublabel}</div>}
    </div>
  )
}

interface ChannelCardProps {
  name: string
  description: string
  features: string[]
  accent: string
}

function ChannelCard({ name, description, features, accent }: ChannelCardProps) {
  return (
    <div className={`p-6 rounded-xl border ${accent} transition-all duration-300 hover:scale-[1.02]`}>
      <h3 className="text-lg font-semibold text-foreground mb-3">{name}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed mb-4">{description}</p>
      <div className="flex flex-wrap gap-2">
        {features.map((feature, i) => (
          <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-background/80 text-muted-foreground border border-border/50">
            {feature}
          </span>
        ))}
      </div>
    </div>
  )
}

export function EnvoyContent() {
  return (
    <article className="prose-container">
      {/* Title Section */}
      <header className="mb-20">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
          Use Case Documentation
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 text-balance">
          ENVOY
        </h1>
        <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed max-w-3xl">
          Integration & Channel Adapter Layer
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8 pt-8 border-t border-border">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Reading time:</span> 5 min
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Version:</span> 2.1
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Last updated:</span> 2026
          </div>
        </div>
      </header>

      {/* Impact Metrics */}
      <section className="mb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard value="4+" label="Channel Types" sublabel="Messaging & web" />
          <MetricCard value="&lt;50ms" label="Latency" sublabel="Message routing" />
          <MetricCard value="100%" label="Context Sync" sublabel="Cross-channel" />
          <MetricCard value="Zero" label="State Lock-in" sublabel="Stateless adapters" />
        </div>
      </section>

      {/* Introduction */}
      <section id="overview" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          01 — Overview
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          The Integration Gateway
        </h2>
        
        <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
          <p>
            Envoy is the Integration Layer in the MADEIRA architecture, connecting AI agents 
            to external communication channels and systems. It serves as the bridge between 
            the intelligence layer and the outside world, handling message routing, protocol 
            translation, and channel-specific adaptations.
          </p>
          <p>
            Built on the <strong className="text-foreground">Channel Adapter pattern</strong>, Envoy enables MADEIRA agents to interact 
            seamlessly across messaging platforms and web interfaces. Each adapter translates platform-specific 
            events into the unified messaging format while respecting platform constraints and capabilities.
          </p>
        </div>

        <KeyInsight>
          Envoy is not a chatbot. It is the gateway that allows intelligent agents to 
          communicate through multiple channels while maintaining consistent behavior and 
          context across all interactions.
        </KeyInsight>
      </section>

      {/* Channel Adapters */}
      <section id="channels" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          02 — Channel Adapters
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Multi-Platform Connectivity
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Each channel adapter encapsulates the complexity of platform-specific protocols, 
          authentication mechanisms, and message formats. The unified internal format enables 
          agents to operate identically regardless of the originating channel.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <ChannelCard
            name="Real-Time Messaging"
            description="Instant message delivery with support for rich media, inline interactions, and threaded conversations. Handles user authentication and session management transparently."
            features={["Instant Delivery", "Rich Media", "Threaded Replies", "Inline Actions"]}
            accent="border-teal-600/30 bg-gradient-to-br from-teal-600/10 to-teal-600/5"
          />
          <ChannelCard
            name="Team Workspaces"
            description="Enterprise collaboration integration with channel mentions, interactive components, and workspace-level permissions. Supports both synchronous and asynchronous communication patterns."
            features={["Channel Mentions", "Interactive UI", "Workspace Auth", "Async Support"]}
            accent="border-emerald-700/30 bg-gradient-to-br from-emerald-700/10 to-emerald-700/5"
          />
          <ChannelCard
            name="Mobile Communication"
            description="Business messaging integration for mobile-first communication. Supports structured templates, media attachments, and delivery confirmations with session management."
            features={["Templates", "Media Support", "Delivery Receipts", "Session Windows"]}
            accent="border-amber-600/30 bg-gradient-to-br from-amber-600/10 to-amber-600/5"
          />
          <ChannelCard
            name="Web Dashboard"
            description="Full-featured web interface with real-time updates. Provides rich chat UI with typing indicators, file handling, and formatted message rendering."
            features={["Real-Time Updates", "File Uploads", "Rich Formatting", "Typing Indicators"]}
            accent="border-rose-600/30 bg-gradient-to-br from-rose-600/10 to-rose-600/5"
          />
        </div>

        <div className="p-6 rounded-xl border border-border bg-card/50">
          <h3 className="text-lg font-semibold text-foreground mb-4">Capability Matrix</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 pr-4 font-semibold text-foreground">Capability</th>
                  <th className="text-center py-3 px-4 font-semibold text-foreground">Messaging</th>
                  <th className="text-center py-3 px-4 font-semibold text-foreground">Workspace</th>
                  <th className="text-center py-3 px-4 font-semibold text-foreground">Mobile</th>
                  <th className="text-center py-3 pl-4 font-semibold text-foreground">Web</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border/50">
                  <td className="py-3 pr-4">Real-Time Sync</td>
                  <td className="text-center py-3 px-4">Yes</td>
                  <td className="text-center py-3 px-4">Events</td>
                  <td className="text-center py-3 px-4">Webhook</td>
                  <td className="text-center py-3 pl-4">WebSocket</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 pr-4">Media Support</td>
                  <td className="text-center py-3 px-4">Full</td>
                  <td className="text-center py-3 px-4">Full</td>
                  <td className="text-center py-3 px-4">Standard</td>
                  <td className="text-center py-3 pl-4">Full</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-3 pr-4">Threading</td>
                  <td className="text-center py-3 px-4">Reply-to</td>
                  <td className="text-center py-3 px-4">Native</td>
                  <td className="text-center py-3 px-4">Session</td>
                  <td className="text-center py-3 pl-4">Conversation</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4">Interactive UI</td>
                  <td className="text-center py-3 px-4">Callbacks</td>
                  <td className="text-center py-3 px-4">Blocks</td>
                  <td className="text-center py-3 px-4">Templates</td>
                  <td className="text-center py-3 pl-4">Custom</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Message Flow */}
      <section id="message-flow" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          03 — Message Pipeline
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Request/Response Flow
        </h2>

        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Every message flows through a standardized pipeline that ensures consistent 
          processing regardless of the originating channel. This abstraction allows agents 
          to focus on intent and response, not platform mechanics.
        </p>

        <div className="bg-gradient-to-br from-card/80 to-card/40 border border-border rounded-2xl p-8 mb-10">
          <h4 className="font-semibold text-foreground mb-6 text-lg">Processing Pipeline</h4>
          <div className="space-y-6">
            {[
              { step: "1", title: "Ingestion", desc: "Channel adapter receives platform event, validates authentication credentials" },
              { step: "2", title: "Normalization", desc: "Transforms to unified message format with enriched metadata" },
              { step: "3", title: "Routing", desc: "Gateway routes to appropriate agent based on binding configuration" },
              { step: "4", title: "Processing", desc: "Agent processes request, may delegate to swarm for complex tasks" },
              { step: "5", title: "Response", desc: "Adapter transforms response to channel-specific format, delivers to user" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-5">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-primary/20 flex items-center justify-center text-sm font-bold text-accent-primary">
                  {item.step}
                </span>
                <div>
                  <p className="font-semibold text-foreground">{item.title}</p>
                  <p className="text-muted-foreground text-sm mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section id="architecture" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          04 — Architecture
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Control Layer Integration
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Envoy adapters connect to the MADEIRA Gateway (Control Layer) which handles 
          authentication, agent routing, and channel multiplexing. All external traffic 
          flows through this secure boundary, with internal services isolated from direct access.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="p-6 rounded-xl border border-border bg-card/50 text-center">
            <div className="text-3xl mb-3">Channels</div>
            <p className="text-sm font-medium text-muted-foreground">External Platforms</p>
            <p className="text-xs text-muted-foreground/70 mt-2">User touchpoints</p>
          </div>
          <div className="p-6 rounded-xl border border-accent-primary/40 bg-gradient-to-br from-accent-primary/10 to-accent-primary/5 text-center">
            <div className="text-3xl mb-3">Envoy</div>
            <p className="text-sm font-medium text-accent-primary">Integration Layer</p>
            <p className="text-xs text-muted-foreground/70 mt-2">Adapters &amp; translation</p>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card/50 text-center">
            <div className="text-3xl mb-3">Gateway</div>
            <p className="text-sm font-medium text-muted-foreground">Control Layer</p>
            <p className="text-xs text-muted-foreground/70 mt-2">Auth &amp; routing</p>
          </div>
        </div>

        <KeyInsight>
          Each channel adapter is stateless. Session state and conversation context are 
          managed by the MADEIRA Memory Server (Intelligence Layer), allowing seamless 
          cross-channel continuity without adapter-level persistence.
        </KeyInsight>
      </section>

      {/* Configuration */}
      <section id="configuration" className="mb-20 scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          05 — Configuration
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Channel Bindings
        </h2>
        
        <div className="space-y-6 text-lg text-muted-foreground leading-relaxed mb-10">
          <p>
            Channels are bound to agents via declarative configuration. Each agent 
            can optionally declare channel bindings for external routing, with credentials 
            managed through secure environment variable injection.
          </p>
          <p>
            The configuration model separates <strong className="text-foreground">agent identity</strong> from <strong className="text-foreground">channel credentials</strong>, 
            allowing the same agent to operate across multiple channels while maintaining 
            consistent behavior and context awareness.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Agent Binding Model</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Identity:</strong> Agent name, persona, behavioral traits</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Capabilities:</strong> Enabled tools and resource access</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary mt-2 shrink-0" />
                <span><strong className="text-foreground">Bindings:</strong> Channel-to-credential mappings</span>
              </li>
            </ul>
          </div>
          <div className="p-6 rounded-xl border border-border bg-card/50">
            <h3 className="text-lg font-semibold text-foreground mb-4">Credential Management</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Secure Injection:</strong> Environment-based credential loading</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Rotation:</strong> Hot-reload support for credential updates</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span><strong className="text-foreground">Isolation:</strong> Per-channel credential scoping</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section id="deployment" className="scroll-mt-40">
        <span className="text-sm font-semibold text-accent-primary uppercase tracking-wider">
          06 — Deployment
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4 mb-8">
          Integration Path
        </h2>
        
        <p className="text-lg text-muted-foreground leading-relaxed mb-10">
          Channel adapters can be added incrementally. Start with the web dashboard for 
          immediate access, then add messaging platforms as organizational needs evolve.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { phase: "Phase 1", title: "Web Interface", desc: "Start with the dashboard for immediate web access. Configure gateway, enable dashboard, begin agent interactions.", time: "Immediate access" },
            { phase: "Phase 2", title: "Team Messaging", desc: "Add real-time messaging or workspace integration for team communication. Configure credentials, update bindings.", time: "1-2 hours setup" },
            { phase: "Phase 3", title: "Multi-Channel", desc: "Add mobile communication, configure cross-channel context sharing, optimize routing for production scale.", time: "Production ready" },
          ].map((item, i) => (
            <div key={i} className="p-6 rounded-xl border border-border bg-card/50">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{item.phase}</span>
              <h3 className="text-lg font-semibold text-foreground mt-2 mb-3">{item.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">{item.desc}</p>
              <p className="text-accent-primary text-sm font-medium">{item.time}</p>
            </div>
          ))}
        </div>
      </section>
    </article>
  )
}
